import { useMemo, useState, useEffect } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Loader2, CalendarPlus, AlertTriangle } from 'lucide-react'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { useAccounts } from '@/api/hooks/useAccounts'
import { useCreateCampaign, useFolderUsage } from '@/api/hooks/usePoster'
import type { PageFollowerGrowth } from '@/api/hooks/usePages'

function tomorrowString(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().split('T')[0]
}

function slugify(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
  pages: PageFollowerGrowth[]
}

export function PublishStuckDialog({ open, onOpenChange, pages }: Props) {
  const { t } = useLanguage()
  const { data: folders = [] } = useMediaFolders()
  const { data: accounts = [] } = useAccounts()
  const createCampaign = useCreateCampaign()

  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [folderChoice, setFolderChoice] = useState<Record<string, string>>({})
  const [postsPerDay, setPostsPerDay] = useState(2)
  const [postTime, setPostTime] = useState('10:00')
  const [startDate, setStartDate] = useState(tomorrowString())
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Conta v1 da página (é quem tem o igUserId que o poster usa)
  const accountByFbPage = useMemo(() => {
    const map = new Map<string, { igUserId: string }>()
    for (const a of accounts) {
      if (a.pageId) map.set(String(a.pageId), { igUserId: a.igUserId })
    }
    return map
  }, [accounts])

  // Pastas candidatas por página: tag em comum OU pasta-raiz cujo nome casa com a
  // tag (na prática as pastas raramente têm tag; a raiz leva o nome da persona)
  const candidatesByPage = useMemo(() => {
    const byId = new Map(folders.map(f => [f.id, f]))
    const rootSlug = (folderId: string): string => {
      let f = byId.get(folderId)
      let guard = 0
      while (f?.parentId && byId.has(f.parentId) && guard++ < 20) f = byId.get(f.parentId)
      return f ? slugify(f.name) : ''
    }

    const map = new Map<string, typeof folders>()
    for (const p of pages) {
      if (!p.tags || p.tags.length === 0) { map.set(p.pageId, []); continue }
      const tagSlugs = p.tags.map(slugify).filter(Boolean)
      const cands = folders.filter(f => {
        if ((f.images?.length ?? 0) === 0) return false
        if ((f.tags ?? []).some(tag => p.tags.includes(tag))) return true
        const rs = rootSlug(f.id)
        return rs !== '' && tagSlugs.some(ts => rs.includes(ts) || ts.includes(rs))
      })
      map.set(p.pageId, cands)
    }
    return map
  }, [pages, folders])

  const allCandidateIds = useMemo(() => {
    const ids = new Set<string>()
    for (const cands of candidatesByPage.values()) cands.forEach(f => ids.add(f.id))
    return Array.from(ids)
  }, [candidatesByPage])

  const { data: folderUsage } = useFolderUsage(open ? allCandidateIds : [])

  const ready = pages.filter(p => accountByFbPage.has(String(p.fbPageId)) && (candidatesByPage.get(p.pageId)?.length ?? 0) > 0)
  const noFolder = pages.filter(p => accountByFbPage.has(String(p.fbPageId)) && (candidatesByPage.get(p.pageId)?.length ?? 0) === 0)
  const noAccount = pages.filter(p => !accountByFbPage.has(String(p.fbPageId)))

  // Sugestão: pasta menos usada NESTA página (depois, menos usada no geral)
  const suggestFolder = (p: PageFollowerGrowth): string => {
    const cands = candidatesByPage.get(p.pageId) ?? []
    if (cands.length === 0) return ''
    const scored = cands.map(f => {
      const usage = folderUsage?.[f.id] ?? {}
      const own = usage[String(p.fbPageId)]?.count ?? 0
      const total = Object.values(usage).reduce((s, u) => s + u.count, 0)
      return { id: f.id, own, total }
    })
    scored.sort((a, b) => a.own - b.own || a.total - b.total)
    return scored[0].id
  }

  useEffect(() => {
    if (!open) return
    // Pré-seleciona as que estão sem conteúdo na fila
    const sel: Record<string, boolean> = {}
    for (const p of ready) sel[p.pageId] = p.pendingPosts === 0
    setSelected(sel)
    setFolderChoice({})
    setPostsPerDay(2)
    setPostTime('10:00')
    setStartDate(tomorrowString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pages.length, folders.length, accounts.length])

  const chosenFolder = (p: PageFollowerGrowth) => folderChoice[p.pageId] || suggestFolder(p)

  const grouped = useMemo(() => {
    const groups = new Map<string, PageFollowerGrowth[]>()
    for (const p of ready) {
      const tag = p.tags[0] ?? '?'
      if (!groups.has(tag)) groups.set(tag, [])
      groups.get(tag)!.push(p)
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [ready])

  const selectedPages = ready.filter(p => selected[p.pageId])
  const totalPosts = selectedPages.reduce((s, p) => {
    const f = folders.find(x => x.id === chosenFolder(p))
    return s + (f?.images?.length ?? 0)
  }, 0)

  const folderLabel = (f: (typeof folders)[number]) => {
    const path = f.parentName ? `${f.parentName}/${f.name}` : f.name
    return `${path} (${f.images?.length ?? 0})`
  }

  const handleSubmit = async () => {
    if (selectedPages.length === 0) { toast.error(t('Selecione ao menos uma pagina')); return }
    setIsSubmitting(true)
    let scheduled = 0
    let failed = 0
    try {
      // Agrupa páginas que usam a mesma pasta numa campanha só
      const byFolder = new Map<string, PageFollowerGrowth[]>()
      for (const p of selectedPages) {
        const fid = chosenFolder(p)
        if (!fid) continue
        if (!byFolder.has(fid)) byFolder.set(fid, [])
        byFolder.get(fid)!.push(p)
      }

      for (const [folderId, groupPages] of byFolder) {
        const folder = folders.find(f => f.id === folderId)
        const imgCount = folder?.images?.length ?? 1
        const accountsPayload = groupPages
          .map(p => accountByFbPage.get(String(p.fbPageId)))
          .filter((a): a is { igUserId: string } => !!a)
          .map(a => ({ igUserId: a.igUserId, platforms: ['fb'] }))
        try {
          const res = await createCampaign.mutateAsync({
            fromLibrary: true,
            folderId,
            accounts: accountsPayload,
            multiDay: true,
            startDate,
            totalDays: Math.ceil(imgCount / postsPerDay),
            postTime,
            imagesPerDay: postsPerDay,
            intervalMin: 30,
            intervalMax: 60,
            contentItems: [],
          })
          scheduled += res.totalPosts
        } catch {
          failed += groupPages.length
        }
      }

      if (scheduled > 0) toast.success(`${scheduled} ${t('posts agendados!')}`)
      if (failed > 0) toast.error(`${failed} ${t('pagina(s) falharam')}`)
      if (scheduled > 0) onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Publicar nas paginas travadas')}</DialogTitle>
          <DialogDescription>
            {t('Conteudo casado pela tag da pagina. Pre-selecionadas as que estao com a fila de posts vazia.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {grouped.map(([tag, groupPages]) => (
            <div key={tag}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-semibold text-muted-foreground">#{tag}</span>
                <Badge variant="outline" className="text-[10px]">{groupPages.length}</Badge>
                <div className="flex-1 border-t border-border/50" />
              </div>
              <div className="space-y-1">
                {groupPages.map(p => (
                  <div key={p.pageId} className="flex items-center gap-2 rounded-md border border-border/50 px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={!!selected[p.pageId]}
                      onChange={e => setSelected(s => ({ ...s, [p.pageId]: e.target.checked }))}
                      className="h-3.5 w-3.5 accent-primary shrink-0"
                    />
                    <span className="text-xs font-medium truncate flex-1 min-w-0">{p.pageName}</span>
                    {p.pendingPosts === 0 ? (
                      <Badge variant="outline" className="text-[9px] shrink-0 bg-red-500/10 border-red-500/20 text-red-500">
                        {t('Sem conteudo')}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground shrink-0">{p.pendingPosts} {t('na fila')}</span>
                    )}
                    <Select value={chosenFolder(p)} onValueChange={v => setFolderChoice(c => ({ ...c, [p.pageId]: v }))}>
                      <SelectTrigger className="h-7 w-[220px] text-xs shrink-0">
                        <SelectValue placeholder={t('Pasta...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {(candidatesByPage.get(p.pageId) ?? []).map(f => (
                          <SelectItem key={f.id} value={f.id} className="text-xs">
                            {folderLabel(f)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {ready.length === 0 && (
            <div className="text-center py-6 text-sm text-muted-foreground">
              {t('Nenhuma pagina travada com tag + pasta correspondente + conta conectada.')}
            </div>
          )}

          {(noFolder.length > 0 || noAccount.length > 0) && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-1 text-xs">
              {noFolder.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <span>
                    <span className="font-medium">{t('Sem pasta com a tag')}:</span>{' '}
                    {noFolder.map(p => p.pageName).join(', ')}
                    {' '}<span className="text-muted-foreground">({t('adicione a tag numa pasta de midia ou na pagina')})</span>
                  </span>
                </div>
              )}
              {noAccount.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <span>
                    <span className="font-medium">{t('Sem conta conectada')}:</span>{' '}
                    {noAccount.map(p => p.pageName).join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Config */}
          <div className="grid grid-cols-3 gap-3 pt-1">
            <div>
              <Label className="text-xs">{t('Posts por dia')}</Label>
              <Input type="number" min={1} max={10} className="h-8 mt-1 text-xs" value={postsPerDay}
                onChange={e => setPostsPerDay(Math.max(1, parseInt(e.target.value) || 1))} />
            </div>
            <div>
              <Label className="text-xs">{t('Horario')}</Label>
              <Input type="time" className="h-8 mt-1 text-xs" value={postTime} onChange={e => setPostTime(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t('Inicio')}</Label>
              <Input type="date" className="h-8 mt-1 text-xs" value={startDate} min={new Date().toISOString().split('T')[0]}
                onChange={e => setStartDate(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Cancelar')}</Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedPages.length === 0}
            className={cn('gap-1.5')}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
            {t('Agendar')} {totalPosts > 0 ? `${totalPosts} posts` : ''} {selectedPages.length > 0 ? `(${selectedPages.length} pag.)` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
